import React, { useEffect, useState } from 'react';
import api from '@/lib/api';
import { PageHeader } from '@/components/app/PageHeader';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Plus } from 'lucide-react';
import { formatINR, fmtDate } from '@/lib/format';
import { StatusPill } from '@/components/app/StatusPill';
import { toast } from 'sonner';

export default function Expenses() {
  const [items, setItems] = useState([]);
  const [props, setProps] = useState([]);
  const [open, setOpen] = useState(false);
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({ property_id: '', category: 'repairs', description: '', amount: 0, expense_date: today, payee: '' });

  const load = () => api.get('/expenses').then(r => setItems(r.data));
  useEffect(() => { load(); api.get('/properties').then(r => setProps(r.data)); }, []);

  const submit = async () => {
    try { await api.post('/expenses', { ...form, amount: Number(form.amount) }); toast.success('Expense added'); setOpen(false); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  return (
    <div>
      <PageHeader title="Expenses" subtitle="Track property expenses" actions={
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button data-testid="add-expense-button"><Plus className="h-4 w-4 mr-1" /> Add expense</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Add expense</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2"><Label>Property</Label><Select value={form.property_id} onValueChange={(v) => setForm({ ...form, property_id: v })}><SelectTrigger data-testid="expense-property-select"><SelectValue placeholder="Select" /></SelectTrigger><SelectContent>{props.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent></Select></div>
              <div><Label>Category</Label><Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="repairs">Repairs</SelectItem><SelectItem value="utilities">Utilities</SelectItem><SelectItem value="society">Society</SelectItem><SelectItem value="tax">Tax</SelectItem><SelectItem value="insurance">Insurance</SelectItem><SelectItem value="vendor">Vendor</SelectItem><SelectItem value="other">Other</SelectItem></SelectContent></Select></div>
              <div><Label>Date</Label><Input type="date" value={form.expense_date} onChange={(e) => setForm({ ...form, expense_date: e.target.value })} /></div>
              <div className="col-span-2"><Label>Description</Label><Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
              <div><Label>Amount</Label><Input type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="expense-amount-input" /></div>
              <div><Label>Payee</Label><Input value={form.payee} onChange={(e) => setForm({ ...form, payee: e.target.value })} /></div>
            </div>
            <DialogFooter><Button onClick={submit} data-testid="expense-submit-button">Save</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      } />
      <Card className="p-0 overflow-x-auto">
        <table className="w-full min-w-[640px] text-sm">
          <thead className="bg-muted/60"><tr>
            <th className="text-left px-3 py-2 text-xs uppercase text-muted-foreground">Date</th>
            <th className="text-left px-3 py-2 text-xs uppercase text-muted-foreground">Property</th>
            <th className="text-left px-3 py-2 text-xs uppercase text-muted-foreground">Category</th>
            <th className="text-left px-3 py-2 text-xs uppercase text-muted-foreground">Description</th>
            <th className="text-right px-3 py-2 text-xs uppercase text-muted-foreground">Amount</th>
            <th className="text-left px-3 py-2 text-xs uppercase text-muted-foreground">Status</th>
          </tr></thead>
          <tbody>
            {items.map((e) => (
              <tr key={e.id} className="border-t hover:bg-muted/40">
                <td className="px-3 py-2">{fmtDate(e.expense_date)}</td>
                <td className="px-3 py-2">{e.property_name}</td>
                <td className="px-3 py-2 capitalize">{e.category}</td>
                <td className="px-3 py-2">{e.description}</td>
                <td className="px-3 py-2 text-right tabular-nums">{formatINR(e.amount)}</td>
                <td className="px-3 py-2"><StatusPill status={e.approval_status} /></td>
              </tr>
            ))}
            {!items.length && <tr><td colSpan={6} className="py-10 text-center text-muted-foreground">No expenses yet.</td></tr>}
          </tbody>
        </table>
      </Card>
    </div>
  );
}
